<template>
  <div class="core-dropdown" :class="['core-dropdown--' + variant, className]">
    <div v-if="variant !== 'simple'" class="core-dropdown__filter" ref="dropdownRef">
      <Icon v-if="variant === 'filter-with-icon'" icon-name="SortIcon" class="core-dropdown__leading-icon" />
      <span v-if="label" :class="['core-dropdown__label', labelClassName || '']">{{ label }}</span>
      <div class="core-dropdown__button-wrap" ref="buttonWrapRef">
        <div
          class="core-dropdown__filter-button"
          :class="{ 'is-disabled': disabled }"
          role="button"
          :tabindex="disabled ? -1 : 0"
          :aria-disabled="disabled"
          @click="!disabled && toggleOpen()"
          @keydown="onKeyActivate"
        >
          <div class="core-dropdown__button-content">
            <span :class="selectedTextClass">
              <template v-if="multiSelect">
                <template v-if="Array.isArray(selectedOptions) && selectedOptions.length">
                  <span
                    v-for="option in selectedOptions.slice(0, 2)"
                    :key="String(getValue(option))"
                    class="core-dropdown__chip"
                  >
                    {{ getLabel(option) }}
                    <span v-if="!disabled" class="core-dropdown__chip-close" @click.stop="removeSelected(option)">
                      <Icon icon-name="CloseIcon" />
                    </span>
                  </span>
                  <span v-if="selectedOptions.length > 2" class="core-dropdown__chip core-dropdown__chip--more">
                    +{{ selectedOptions.length - 2 }}
                  </span>
                </template>
                <template v-else>
                  {{ placeholder }}
                </template>
              </template>
              <template v-else>
                {{ selectedOptions ? getLabel(selectedOptions) : placeholder }}
              </template>
            </span>
            <Icon v-if="showIcon" icon-name="Dropdown" class="core-dropdown__caret" />
          </div>
        </div>

        <div class="core-dropdown__menu" :class="menuDirectionClass" v-show="isOpen">
          <div v-if="emptyMessageVisible" class="core-dropdown__empty">No results found</div>
          <template v-else>
            <div
              v-for="option in displayOptions"
              :key="String(getValue(option))"
              class="core-dropdown__option"
              :class="optionClass(option)"
              role="button"
              tabindex="0"
              @mousedown="handleOptionClick(option, $event)"
              @keydown="onKeyActivate"
            >
              <div v-if="!renderOption && !$slots.option" class="core-dropdown__option-content">
                <span class="core-dropdown__option-label">{{ getLabel(option) }}</span>
                <span v-if="showCounts && getCount && getCount(option) !== undefined" class="core-dropdown__option-count">
                  {{ getCount(option) }}
                </span>
              </div>
              <component v-else-if="renderOption" :is="renderOption" :option="option" :selected="isSelected(option)" />
              <slot v-else name="option" :option="option" :selected="isSelected(option)" />
            </div>
          </template>
          <div
            v-if="shouldShowAddNewOption"
            class="core-dropdown__add-option"
            role="button"
            tabindex="0"
            @mousedown="handleAddNewOption"
            @keydown="onKeyActivate"
          >
            <span class="core-dropdown__option-label">
              +
              <span v-html="renderHighlightedText(getDynamicLabel(addNewOptionLabel, search), search)"></span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="core-dropdown__simple" ref="dropdownRef">
      <label v-if="label" :class="['core-dropdown__label', hasError ? 'core-dropdown__label--error' : '']">
        {{ label }}<span v-if="required" class="core-dropdown__required">*</span>
      </label>
      <div class="core-dropdown__button-wrap" ref="buttonWrapRef">
        <div
          data-testid="dropdown-toggle"
          class="core-dropdown__simple-button"
          :class="{ 'is-disabled': disabled, 'is-error': hasError }"
          role="button"
          :tabindex="disabled ? -1 : 0"
          :aria-disabled="disabled"
          @click="!disabled && toggleOpen()"
          @keydown="onKeyActivate"
          @blur="handleBlur"
        >
          <div class="core-dropdown__button-content">
            <span :class="selectedTextClass">
              <template v-if="multiSelect">
                <template v-if="Array.isArray(selectedOptions) && selectedOptions.length">
                  <span
                    v-for="option in selectedOptions.slice(0, 2)"
                    :key="String(getValue(option))"
                    class="core-dropdown__chip"
                  >
                    {{ getLabel(option) }}
                    <span v-if="!disabled" class="core-dropdown__chip-close" @click.stop="removeSelected(option)">
                      <Icon icon-name="CloseIcon" />
                    </span>
                  </span>
                  <span v-if="selectedOptions.length > 2" class="core-dropdown__chip core-dropdown__chip--more">
                    +{{ selectedOptions.length - 2 }}
                  </span>
                </template>
                <template v-else>
                  {{ placeholder }}
                </template>
              </template>
              <template v-else>
                {{ selectedOptions ? getLabel(selectedOptions) : placeholder }}
              </template>
            </span>
            <div class=" bg-slate-600">
              <Icon icon-name="Dropdown" />
            </div>
          </div>
        </div>

        <div class="core-dropdown__menu core-dropdown__menu--simple" :class="menuDirectionClass" v-show="isOpen">
          <div v-if="openDirection === 'down' && searchable" class="core-dropdown__search">
            <input
              type="text"
              v-model="search"
              class="core-dropdown__search-input"
              placeholder="Search..."
              autofocus
            />
          </div>

          <div v-if="emptyMessageVisible" class="core-dropdown__empty">No results found</div>

          <div
            v-for="option in displayOptions"
            :key="String(getValue(option))"
            class="core-dropdown__option"
            :class="simpleOptionClass(option)"
            role="button"
            tabindex="0"
            @mousedown="handleOptionClick(option, $event)"
            @keydown="onKeyActivate"
          >
            <div v-if="!renderOption && !$slots.option" class="core-dropdown__option-content core-dropdown__option-content--simple">
              <span class="core-dropdown__option-label">{{ getLabel(option) }}</span>
              <span v-if="showCounts && getCount && getCount(option) !== undefined" class="core-dropdown__option-count">
                {{ getCount(option) }}
              </span>
            </div>
            <component v-else-if="renderOption" :is="renderOption" :option="option" :selected="isSelected(option)" />
            <slot v-else name="option" :option="option" :selected="isSelected(option)" />
          </div>

          <div v-if="shouldShowAddNewOption" class="core-dropdown__add-option" role="button" tabindex="0" @mousedown="handleAddNewOption" @keydown="onKeyActivate">
            <span class="core-dropdown__option-label">
              +
              <span v-html="renderHighlightedText(getDynamicLabel(addNewOptionLabel, search), search)"></span>
            </span>
          </div>

          <div v-if="openDirection === 'up' && searchable" class="core-dropdown__search core-dropdown__search--bottom">
            <input
              type="text"
              v-model="search"
              class="core-dropdown__search-input"
              placeholder="Search..."
              autofocus
            />
          </div>
        </div>
      </div>
      <span v-if="hasError" class="core-dropdown__error">{{ errorMessage }}</span>
    </div> 
  </div>
</template>

<script>
import Icon from "./Icon.vue";

const DROPDOWN_HEIGHT = 240;

export default {
  name: "Dropdown",
  components: {
    Icon
  },
  props: {
    label: String,
    options: {
      type: Array,
      default() {
        return [];
      }
    },
    value: {
      type: [Array, Object, String, Number, Boolean, null],
      default: null
    },
    onChange: Function,
    getLabel: {
      type: Function,
      required: true
    },
    getValue: {
      type: Function,
      required: true
    },
    getCount: Function,
    placeholder: {
      type: String,
      default: "Select an option"
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    showCounts: {
      type: Boolean,
      default: false
    },
    className: {
      type: [String, Array, Object],
      default: ""
    },
    disabled: Boolean,
    variant: {
      type: String,
      default: "simple"
    },
    searchable: Boolean,
    renderOption: [Object, Function],
    multiSelect: Boolean,
    required: Boolean,
    error: String,
    onBlur: Function,
    addNewOption: Boolean,
    addNewOptionLabel: {
      type: String,
      default: "Add new option"
    },
    onAddNewOption: Function,
    labelClassName: {
      type: [String, Array, Object],
      default: ""
    }
  },
  data() {
    return {
      isOpen: false,
      openDirection: "down",
      search: "",
      internalError: "",
      dropdownHeight: DROPDOWN_HEIGHT
    };
  },
  computed: {
    hasError() {
      return Boolean(this.error) || Boolean(this.internalError);
    },
    errorMessage() {
      return this.error || this.internalError || "";
    },
    selectedOptions() {
      if (this.multiSelect) {
        if (!Array.isArray(this.value)) {
          return [];
        }
        const currentValues = this.value
          .map((item) => this.normalizeOptionValue(item))
          .filter((item) => item !== undefined);
        if (!currentValues.length) {
          return [];
        }
        return this.options.filter((option) => {
          const optionValue = this.normalizeOptionValue(option);
          return optionValue !== undefined && currentValues.indexOf(optionValue) !== -1;
        });
      }
      if (Array.isArray(this.value)) {
        return undefined;
      }
      const singleValue = this.normalizeOptionValue(this.value);
      if (singleValue === undefined) {
        return undefined;
      }
      return this.options.find((option) => this.normalizeOptionValue(option) === singleValue);
    },
    filteredOptions() {
      if (this.variant === "simple" && this.searchable) {
        const searchTerm = this.search.toLowerCase();
        return this.options.filter((option) => {
          const label = this.getLabel(option);
          return typeof label === "string" && label.toLowerCase().indexOf(searchTerm) !== -1;
        });
      }
      return this.options;
    },
    shouldShowAddNewOption() {
      return this.addNewOption && this.searchable && this.search.trim() !== "" && this.filteredOptions.length === 0;
    },
    displayOptions() {
      return this.openDirection === "up" ? this.filteredOptions.slice().reverse() : this.filteredOptions;
    },
    selectedTextClass() {
      const hasSelection = this.multiSelect
        ? Array.isArray(this.selectedOptions) && this.selectedOptions.length > 0
        : Boolean(this.selectedOptions);
      return hasSelection ? "core-dropdown__selected-text--active" : "core-dropdown__selected-text--placeholder";
    },
    menuDirectionClass() {
      return this.openDirection === "up" ? "core-dropdown__menu--up" : "core-dropdown__menu--down";
    },
    emptyMessageVisible() {
      if (this.searchable) {
        return !this.filteredOptions.length && !this.shouldShowAddNewOption;
      }
      return !this.options.length;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (this.required) {
          const hasValue = this.multiSelect
            ? Array.isArray(newValue) && newValue.length > 0
            : newValue !== undefined && newValue !== null && newValue !== "";
          if (hasValue) {
            this.internalError = "";
          }
        }
      }
    },
    error(newValue) {
      if (newValue) {
        this.internalError = "";
      }
    },
    isOpen(newValue) {
      if (newValue && this.$refs.buttonWrapRef) {
        const rect = this.$refs.buttonWrapRef.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        this.openDirection = spaceBelow < this.dropdownHeight && spaceAbove > this.dropdownHeight ? "up" : "down";
      }
    }
  },
  mounted() {
    this.handleDocumentClick = (event) => {
      const dropdown = this.$refs.dropdownRef;
      if (dropdown && !dropdown.contains(event.target)) {
        this.isOpen = false;
      }
    };
    document.addEventListener("mousedown", this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleDocumentClick);
  },
  methods: {
    normalizeOptionValue(input) {
      if (input === null || input === undefined) {
        return undefined;
      }
      if (typeof input === "object") {
        try {
          return this.getValue(input);
        } catch (error) {
          return undefined;
        }
      }
      return input;
    },
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    removeSelected(option) {
      if (!this.multiSelect) {
        return;
      }
      const optionValue = this.normalizeOptionValue(option);
      if (optionValue === undefined) {
        return;
      }
      const nextValue = Array.isArray(this.value) ? this.value.slice() : [];
      const index = nextValue.findIndex((item) => this.normalizeOptionValue(item) === optionValue);
      if (index !== -1) {
        nextValue.splice(index, 1);
        this.$emit("update:value", nextValue);
        if (this.onChange) {
          this.onChange(nextValue);
        }
      }
    },
    handleOptionClick(option, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (this.multiSelect) {
        const optionValue = this.normalizeOptionValue(option);
        if (optionValue === undefined) {
          return;
        }
        const nextValue = Array.isArray(this.value) ? this.value.slice() : [];
        const index = nextValue.findIndex((item) => this.normalizeOptionValue(item) === optionValue);
        if (index !== -1) {
          nextValue.splice(index, 1);
        } else {
          nextValue.push(option);
        }
        this.$emit("update:value", nextValue);
        if (this.onChange) {
          this.onChange(nextValue);
        }
      } else {
        this.$emit("update:value", option);
        if (this.onChange) {
          this.onChange(option);
        }
        this.isOpen = false;
      }
      this.search = "";
    },
    handleAddNewOption(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const searchTerm = this.search;
      this.isOpen = false;
      if (this.onAddNewOption) {
        this.onAddNewOption(searchTerm);
      }
      this.search = "";
    },
    handleBlur() {
      if (this.required) {
        const hasValue = this.multiSelect
          ? Array.isArray(this.value) && this.value.length > 0
          : this.value !== undefined && this.value !== null && this.value !== "";
        if (!hasValue) {
          const labelText = this.label || "This field";
          this.internalError = labelText + " is required.";
        }
      }
      if (this.onBlur) {
        this.onBlur();
      }
    },
    onKeyActivate(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        const target = event.currentTarget;
        if (target && typeof target.click === "function") {
          target.click();
        }
      }
    },
    isSelected(option) {
      const optionValue = this.normalizeOptionValue(option);
      if (optionValue === undefined) {
        return false;
      }
      if (this.multiSelect) {
        return Array.isArray(this.value) && this.value.some((item) => this.normalizeOptionValue(item) === optionValue);
      }
      return optionValue === this.normalizeOptionValue(this.value);
    },
    renderHighlightedText(text, searchTerm) {
      if (!searchTerm || !searchTerm.trim()) {
        return text;
      }
      const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp("(" + escaped + ")", "gi");
      return text.replace(regex, "<strong>$1</strong>");
    },
    getDynamicLabel(label, searchTerm) {
      if (!searchTerm || !searchTerm.trim()) {
        return label;
      }
      return label.replace(/\{\{search\}\}/g, searchTerm);
    },
    optionClass(option) {
      return { "is-selected": this.isSelected(option) };
    },
    simpleOptionClass(option) {
      return { "is-selected": this.isSelected(option) };
    }
  }
};
</script>

<style scoped>
.core-dropdown {
  position: relative;
  font-size: 14px;
  color: #1f2933;
}

.core-dropdown__filter {
  display: flex;
  align-items: center;
  gap: 8px;
}

.core-dropdown__label {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
}

.core-dropdown__label--error {
  color: #fa0202;
}

.core-dropdown__required {
  color: #fa0202;
  margin-left: 2px;
}

.core-dropdown__button-wrap {
  position: relative;
}

.core-dropdown__filter-button,
.core-dropdown__simple-button {
  border: 1px solid #d1d5db;
  background-color: #ffffff;
  border-radius: 4px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease;
  padding-left: 0.5rem;
}

.core-dropdown__filter-button.is-disabled,
.core-dropdown__simple-button.is-disabled {
  background-color: #f5f5f5;
  color: #94a3b8;
  cursor: not-allowed;
}

.core-dropdown__simple-button.is-error {
  border-color: #fa0202;
  background-color: #fdecec;
}

.core-dropdown__button-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.core-dropdown__selected-text {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.core-dropdown__selected-text--active {
  color: #0f172a;
}

.core-dropdown__selected-text--placeholder {
  color: #64748b;
}

.core-dropdown__chip {
  display: inline-flex;
  align-items: center;
  background-color: #f1f5f9;
  color: #1e293b;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
}

.core-dropdown__chip--more {
  background-color: #e2e8f0;
}

.core-dropdown__chip-close {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  cursor: pointer;
}

.core-dropdown__caret,
.core-dropdown__caret-holder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
}

.core-dropdown__menu {
  position: absolute;
  left: 0;
  min-width: 180px;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.08);
  max-height: 240px;
  overflow-y: auto;
  padding: 8px;
  z-index: 20;
  transform-origin: top;
}

.core-dropdown__menu--simple {
  width: 100%;
}

.core-dropdown__menu--up {
  bottom: 100%;
  margin-bottom: 6px;
}

.core-dropdown__menu--down {
  top: 100%;
  margin-top: 6px;
}

.core-dropdown__empty {
  padding: 12px;
  font-size: 12px;
  color: #94a3b8;
}

.core-dropdown__option {
  padding: 10px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.core-dropdown__option:hover,
.core-dropdown__option:focus {
  background-color: #f1f5f9;
}

.core-dropdown__option.is-selected {
  background-color: #e6f6fe;
  color: #0f172a;
}

.core-dropdown__option-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.core-dropdown__option-content--simple {
  width: 100%;
}

.core-dropdown__option-label {
  font-weight: 500;
}

.core-dropdown__option-count {
  font-size: 12px;
  color: #94a3b8;
  margin-left: 8px;
}

.core-dropdown__add-option {
  margin-top: 8px;
  padding: 10px 12px;
  border-top: 1px solid #e2e8f0;
  cursor: pointer;
}

.core-dropdown__add-option:hover,
.core-dropdown__add-option:focus {
  background-color: #f1f5f9;
}

.core-dropdown__search {
  padding: 8px;
  background-color: #ffffff;
}

.core-dropdown__search--bottom {
  border-top: 1px solid #e2e8f0;
}

.core-dropdown__search-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  outline: none;
}

.core-dropdown__search-input:focus {
  border-color: #0284c7;
}

.core-dropdown__error {
  margin-top: 4px;
  display: inline-block;
  border: 1px solid #fa0202;
  color: #fa0202;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  position: relative;
}

.core-dropdown__error::before {
  content: "";
  position: absolute;
  top: -7px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #ffffff;
  z-index: 1;
}

.core-dropdown__error::after {
  content: "";
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #fa0202;
  z-index: 0;
}
</style>
